// frontend/client/src/components/Calendar.jsx
// FIXED: Added useLocation to catch AICoach navigate state and auto-open modal
// When AICoach calls navigate('/dashboard/calendar', { state: { aiSuggestion } })
// the modal now automatically opens pre-filled with the AI suggestion.

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Plus, Instagram, MessageCircle, Linkedin, Twitter, Youtube,
  X, Check, Sparkles, ChevronLeft, ChevronRight, Info
} from 'lucide-react';
import { api } from '../services/apiClient';

const PLATFORM_ICONS = {
  instagram: { icon: Instagram, color: '#E1306C', name: 'Instagram' },
  tiktok:    { icon: MessageCircle, color: '#000000', name: 'TikTok' },
  linkedin:  { icon: Linkedin, color: '#0A66C2', name: 'LinkedIn' },
  twitter:   { icon: Twitter, color: '#000000', name: 'Twitter/X' },
  youtube:   { icon: Youtube, color: '#FF0000', name: 'YouTube' },
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const CAL_CSS = `
  .cal-root{display:flex;flex-direction:column;gap:20px;padding:24px;font-family:var(--font-body);min-height:100vh;box-sizing:border-box}
  .cal-header{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px}
  .cal-title{font-family:var(--font-display);font-size:22px;font-weight:700;color:var(--text-main);margin:0;letter-spacing:-0.02em}
  .cal-sub{font-size:13px;color:var(--text-muted);margin:0}
  .cal-month-nav{display:flex;align-items:center;gap:12px}
  .cal-month-label{font-family:var(--font-display);font-size:16px;font-weight:700;color:var(--text-main);min-width:160px;text-align:center}
  .cal-nav-btn{width:32px;height:32px;border-radius:8px;border:1px solid var(--border-light);background:var(--bg-surface);color:var(--text-main);display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all 0.15s}
  .cal-nav-btn:hover{border-color:var(--brand-primary);color:var(--brand-primary)}
  .cal-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:6px}
  .cal-day-header{text-align:center;font-size:11px;font-weight:700;color:var(--text-muted);text-transform:uppercase;padding:8px 0;letter-spacing:0.06em}
  .cal-cell{background:var(--bg-surface);border:1px solid var(--border-light);border-radius:12px;padding:8px;min-height:90px;display:flex;flex-direction:column;gap:4px;transition:border-color 0.15s;cursor:default}
  .cal-cell:hover{border-color:var(--brand-border)}
  .cal-cell.today{border-color:var(--brand-primary);background:var(--brand-secondary)}
  .cal-cell.empty{background:var(--bg-canvas);opacity:0.4}
  .cal-cell-date{font-size:12px;font-weight:700;color:var(--text-muted);margin-bottom:2px}
  .cal-cell.today .cal-cell-date{color:var(--brand-secondary-contrast, var(--brand-primary))}
  .cal-event{border-radius:6px;padding:4px 7px;font-size:10px;cursor:pointer;transition:transform 0.1s;display:flex;align-items:center;gap:4px;overflow:hidden}
  .cal-event:hover{transform:translateY(-1px);box-shadow:var(--shadow-sm)}
  .cal-event-text{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex:1}
  .cal-modal-overlay{position:fixed;inset:0;background:rgba(15,23,42,0.5);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;z-index:9999;padding:20px}
  .cal-modal{background:var(--bg-surface);width:100%;max-width:520px;border-radius:20px;padding:28px;box-shadow:var(--shadow-xl);border:1px solid var(--border-light)}
  .cal-modal-header{display:flex;justify-content:space-between;align-items:center;margin-bottom:22px}
  .cal-modal-title{font-family:var(--font-display);font-size:18px;font-weight:700;color:var(--text-main);margin:0;display:flex;align-items:center;gap:8px}
  .cal-input,.cal-select{width:100%;padding:11px 14px;border-radius:10px;border:1px solid var(--border-medium);background:var(--bg-canvas);font-size:14px;font-family:var(--font-body);color:var(--text-main);outline:none;transition:border-color 0.15s;box-sizing:border-box;display:block}
  .cal-input:focus,.cal-select:focus{border-color:var(--brand-primary)}
  .cal-label{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:var(--text-muted);margin-bottom:6px;display:block}
  .cal-field{margin-bottom:16px}
  .cal-ai-banner{background:var(--brand-secondary);border:1px solid var(--brand-border);border-radius:10px;padding:12px 14px;display:flex;align-items:center;gap:10px;margin-bottom:20px;font-size:13px;font-weight:600;color:var(--brand-secondary-contrast, var(--brand-primary))}
  .cal-btn-primary{background:linear-gradient(135deg,var(--brand-primary),var(--brand-accent));color:var(--brand-primary-contrast, white);border:none;padding:12px;border-radius:12px;width:100%;font-weight:700;font-size:14px;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;font-family:var(--font-body);transition:opacity 0.2s}
  .cal-btn-primary:hover{opacity:0.9}
  .cal-header-actions{display:flex;gap:10px}
  .cal-action-btn{display:flex;align-items:center;gap:6px;padding:9px 16px;border-radius:10px;font-size:12px;font-weight:700;cursor:pointer;transition:all 0.15s;font-family:var(--font-body)}
`;

export default function Calendar() {
  const location = useLocation();

  const today     = new Date();
  const [year,    setYear]     = useState(today.getFullYear());
  const [month,   setMonth]    = useState(today.getMonth());
  const [events,  setEvents]   = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newEvent,  setNewEvent]  = useState({
    content: '', platform: 'linkedin', time: '12:00', date: '', isAiGenerated: false
  });

  // Real fetch, replacing the local-only mock array this used to start with.
  // Runs once on mount — this is what makes "schedule from Feed Preview,
  // see it show up here" actually true, rather than two components
  // coincidentally agreeing on fake local data.
  const fetchScheduledEvents = async () => {
    setLoadingEvents(true);
    try {
      const res = await api.get('/scheduling/list');
      if (res.success) setEvents(res.events || []);
    } catch (err) {
      console.error('[Calendar] Failed to load scheduled posts:', err);
    } finally {
      setLoadingEvents(false);
    }
  };

  useEffect(() => {
    fetchScheduledEvents();
  }, []);

  // ✅ FIXED: Catch AICoach navigate state and auto-open modal pre-filled
  useEffect(() => {
    const aiSuggestion = location.state?.aiSuggestion;
    if (aiSuggestion) {
      setNewEvent({
        content:       aiSuggestion.content      || '',
        platform:      aiSuggestion.platform     || 'linkedin',
        time:          aiSuggestion.time         || '12:00',
        date:          aiSuggestion.date         || '',
        isAiGenerated: aiSuggestion.isAiGenerated || true,
      });
      setShowModal(true);
      // Clear location state so modal doesn't re-open on refresh
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [location.state]);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  // Build calendar grid
  const firstDay    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells       = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isToday = (d) =>
    d === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  const eventsForDay = (d) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    return events.filter(ev => ev.date === dateStr);
  };

  const handleAddEvent = () => {
    if (!newEvent.content || !newEvent.date) return;
    setEvents(prev => [...prev, { ...newEvent, id: Date.now().toString() }]);
    setShowModal(false);
    setNewEvent({ content: '', platform: 'linkedin', time: '12:00', date: '', isAiGenerated: false });
  };

  const openNewEvent = (d) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    setNewEvent({ content: '', platform: 'linkedin', time: '12:00', date: dateStr, isAiGenerated: false });
    setShowModal(true);
  };

  return (
    <div className="cal-root">
      <style>{CAL_CSS}</style>

      {/* Header */}
      <div className="cal-header">
        <div>
          <h1 className="cal-title">Content Calendar</h1>
          <p className="cal-sub">Orchestrate your cross-platform strategy</p>
        </div>
        <div className="cal-header-actions">
          <div className="cal-month-nav">
            <button className="cal-nav-btn" onClick={prevMonth}><ChevronLeft size={14}/></button>
            <span className="cal-month-label">{MONTHS[month]} {year}</span>
            <button className="cal-nav-btn" onClick={nextMonth}><ChevronRight size={14}/></button>
          </div>
          <button
            className="cal-action-btn"
            onClick={() => setShowModal(true)}
            style={{ background: 'var(--brand-primary)', color: 'var(--brand-primary-contrast, white)', border: 'none', boxShadow: 'var(--shadow-brand)' }}
          >
            <Plus size={14}/> Schedule Post
          </button>
        </div>
      </div>

      {/* Beta disclaimer — Vercel Cron on Hobby plan only checks for due
          scheduled posts once per day, so an exact time isn't guaranteed
          during beta. Upgrading to Vercel Pro removes this limitation. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--brand-secondary)', border: '1px solid var(--brand-border)', borderRadius: 10, fontSize: 12, color: 'var(--brand-secondary-contrast, var(--brand-primary))' }}>
        <Info size={14} style={{ flexShrink: 0 }}/>
        <span>Beta limitation: scheduled posts are checked once daily, so exact publish time isn't guaranteed yet — this is being improved.</span>
      </div>

      {/* Calendar Grid */}
      <div className="cal-grid">
        {DAYS.map(d => <div key={d} className="cal-day-header">{d}</div>)}
        {cells.map((d, i) => (
          <div
            key={i}
            className={`cal-cell ${d === null ? 'empty' : ''} ${d && isToday(d) ? 'today' : ''}`}
            onClick={() => d && openNewEvent(d)}
            style={{ cursor: d ? 'pointer' : 'default' }}
          >
            {d && <span className="cal-cell-date">{d}</span>}
            {d && eventsForDay(d).map(ev => {
              const cfg = PLATFORM_ICONS[ev.platform];
              const Icon = cfg?.icon;
              return (
                <div
                  key={ev.id}
                  className="cal-event"
                  style={{ background: `${cfg?.color}18`, border: `1px solid ${cfg?.color}30` }}
                  onClick={e => e.stopPropagation()}
                >
                  {Icon && <Icon size={10} color={cfg.color} style={{ flexShrink: 0 }}/>}
                  <span className="cal-event-text" style={{ color: 'var(--text-main)' }}>{ev.time} — {ev.content}</span>
                  {ev.isAiGenerated && <Sparkles size={9} color="var(--brand-primary)" style={{ flexShrink: 0 }}/>}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="cal-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="cal-modal" onClick={e => e.stopPropagation()}>
            <div className="cal-modal-header">
              <h2 className="cal-modal-title">
                {newEvent.isAiGenerated
                  ? <><Sparkles size={18} color="var(--brand-primary)"/> AI Suggested Post</>
                  : <><Plus size={18}/> Schedule Post</>
                }
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                <X size={20}/>
              </button>
            </div>

            {newEvent.isAiGenerated && (
              <div className="cal-ai-banner">
                <Sparkles size={16}/>
                This post was suggested by your AI Coach based on your analytics
              </div>
            )}

            {!newEvent.isAiGenerated && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--bg-canvas)', border: '1px solid var(--border-light)', borderRadius: 10, fontSize: 12, color: 'var(--text-muted)', marginBottom: 20 }}>
                <Info size={14} style={{ flexShrink: 0 }}/>
                <span>This is a planning note only — to schedule a real post with your video or image attached, use "Schedule for later" from Feed Preview instead.</span>
              </div>
            )}

            <div className="cal-field">
              <label className="cal-label">Platform</label>
              <select
                className="cal-select"
                value={newEvent.platform}
                onChange={e => setNewEvent(p => ({ ...p, platform: e.target.value }))}
              >
                {Object.entries(PLATFORM_ICONS).map(([key, data]) => (
                  <option key={key} value={key}>{data.name}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="cal-field">
                <label className="cal-label">Date</label>
                <input
                  type="date"
                  className="cal-input"
                  value={newEvent.date}
                  onChange={e => setNewEvent(p => ({ ...p, date: e.target.value }))}
                />
              </div>
              <div className="cal-field">
                <label className="cal-label">Time</label>
                <input
                  type="time"
                  className="cal-input"
                  value={newEvent.time}
                  onChange={e => setNewEvent(p => ({ ...p, time: e.target.value }))}
                />
              </div>
            </div>

            <div className="cal-field">
              <label className="cal-label">Caption / Hook</label>
              <textarea
                className="cal-input"
                rows={5}
                value={newEvent.content}
                onChange={e => setNewEvent(p => ({ ...p, content: e.target.value }))}
                placeholder="What's your post about?"
                style={{ resize: 'vertical' }}
              />
            </div>

            <button className="cal-btn-primary" onClick={handleAddEvent}>
              <Check size={16}/> Schedule for Publishing
            </button>
          </div>
        </div>
      )}
    </div>
  );
}